import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SmartDisplayIcon from "@mui/icons-material/SmartDisplay";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { useUserContext } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";
import { useProjectContext } from "../../context/ProjectContext";
import ActorsDialog from "../../components/ActorsDialog";
import {
  Actor,
  CustomPhotoAvatar,
  GeneratedVideo,
  VideoAgentSession,
  VideoAgentStyle,
} from "../../context/types";
import {
  createVideoAgentSession,
  generateVideoAgent,
  getVideoAgentSession,
  getVideoAgentSessionStyles,
  getVideoAgentVideos,
  postVideoAgentSessionMessage,
  uploadBackgroundAssetV2,
  updateGeneratedVideoStatus,
} from "../../client/api";
import useStyles from "./styles";

const POLL_INTERVAL = 5000;

const VideoAgent: React.FC = () => {
  const classes = useStyles();
  const { user, currentWorkspace } = useUserContext();
  const { addNotification } = useNotification();
  const { avatars } = useProjectContext();

  // Form state
  const [prompt, setPrompt] = useState("");
  const [selectedActor, setSelectedActor] = useState<Actor | null>(null);
  const [selectedLabActor, setSelectedLabActor] = useState<CustomPhotoAvatar | null>(null);
  const [orientation, setOrientation] = useState<"landscape" | "portrait">("portrait");
  const [duration, setDuration] = useState<number | "">(30);
  const [styleId, setStyleId] = useState<string>("");
  const [mode, setMode] = useState<"generate" | "chat">("generate");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [isPostingSessionMessage, setIsPostingSessionMessage] = useState(false);
  const [isFetchingStyles, setIsFetchingStyles] = useState(false);
  const [referenceImages, setReferenceImages] = useState<{
    previewUrl: string;
    publicUrl: string;
  }[]>([]);
  const [isUploadingReferenceImages, setIsUploadingReferenceImages] = useState(false);
  const referenceImageInputRef = useRef<HTMLInputElement | null>(null);

  // Dialogs
  const [actorsDialogOpen, setActorsDialogOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<GeneratedVideo | null>(null);

  // Videos list
  const [videos, setVideos] = useState<GeneratedVideo[]>([]);
  const [isFetchingVideos, setIsFetchingVideos] = useState(false);
  const [styles, setStyles] = useState<VideoAgentStyle[]>([]);
  const [session, setSession] = useState<VideoAgentSession | null>(null);
  const [sessionInput, setSessionInput] = useState<string>("");
  const sessionRef = useRef<VideoAgentSession | null>(null);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Keep a ref in sync with videos so the polling interval always reads fresh data
  // without depending on the videos state variable (avoids stale closure / interval teardown loop)
  const videosRef = useRef<GeneratedVideo[]>([]);
  useEffect(() => {
    videosRef.current = videos;
  }, [videos]);

  const mergeGeneratedVideos = useCallback(
    (existing: GeneratedVideo[], additions: GeneratedVideo[]) => {
      const ids = new Set(existing.map((video) => video._id));
      const merged = [...existing];
      for (const video of additions) {
        if (!ids.has(video._id)) {
          merged.unshift(video);
          ids.add(video._id);
        }
      }
      return merged;
    },
    []
  );

  const fetchStyles = useCallback(async () => {
    setIsFetchingStyles(true);
    try {
      const data = await getVideoAgentSessionStyles("cinematic", 20);
      setStyles(data.data || []);
    } catch {
      // ignore style fetch errors
    } finally {
      setIsFetchingStyles(false);
    }
  }, []);

  const fetchVideos = useCallback(async () => {
    if (!currentWorkspace?._id) return;
    setIsFetchingVideos(true);
    try {
      const data = await getVideoAgentVideos(currentWorkspace._id);
      setVideos(data);
    } catch {
      // silently fail on background refresh
    } finally {
      setIsFetchingVideos(false);
    }
  }, [currentWorkspace?._id]);

  const pollSession = useCallback(async () => {
    if (!sessionRef.current?._id) return;
    try {
      const response = await getVideoAgentSession(sessionRef.current._id);
      const refreshedSession = response.session || response;
      setSession(refreshedSession);
      setVideos((prev) =>
        mergeGeneratedVideos(
          prev,
          refreshedSession.videos?.map((item: { generatedVideo: GeneratedVideo }) => item.generatedVideo) ?? []
        )
      );
    } catch {
      // ignore transient session sync failures
    }
  }, [mergeGeneratedVideos]);

  useEffect(() => {
    fetchVideos();
    fetchStyles();
  }, [fetchVideos, fetchStyles]);

  useEffect(() => {
    if (!session?._id) return;
    const intervalId = setInterval(() => {
      if (!sessionRef.current) return;
      if (sessionRef.current.status === "completed" || sessionRef.current.status === "failed") return;
      pollSession();
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [session?._id, pollSession]);

  // Single stable interval — set up once on mount, reads current state via ref.
  // Avoids the teardown/recreation cycle that can silently drop the interval in production.
  useEffect(() => {
    const intervalId = setInterval(async () => {
      const pending = videosRef.current.filter(
        (v) => v.status !== "completed" && v.status !== "success" && v.status !== "failed"
      );
      if (pending.length === 0) return;

      for (const video of pending) {
        try {
          const updated: GeneratedVideo = await updateGeneratedVideoStatus(video.heygenId);
          if (updated.status !== video.status) {
            setVideos((prev) => prev.map((v) => (v._id === updated._id ? updated : v)));
            setSelectedVideo((prev) => (prev?._id === updated._id ? updated : prev));
          }
        } catch {
          // ignore individual poll errors
        }
      }
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve which avatar_id to send: lab actor (look's photo_avatar_id) takes priority
  const resolvedAvatarId = selectedLabActor?.photo_avatar_id ?? selectedActor?.avatar_id;

  // Display info for the avatar button
  const avatarDisplayImage =
    selectedLabActor?.selected_image_url || selectedActor?.preview_image_url || null;
  const avatarDisplayName = selectedLabActor?.name || selectedActor?.name || "Select Avatar";

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      addNotification("Please enter a prompt", "warning");
      return;
    }
    if (!currentWorkspace?._id || !user?._id) return;

    setIsGenerating(true);
    try {
      const created: GeneratedVideo = await generateVideoAgent({
        prompt: prompt.trim(),
        workspace: currentWorkspace._id,
        generatedBy: user._id,
        name: prompt.trim().slice(0, 60),
        ...(resolvedAvatarId ? { avatar_id: resolvedAvatarId } : {}),
        ...(duration !== "" && Number(duration) >= 5 ? { duration_sec: Number(duration) } : {}),
        orientation,
        aspect_ratio: orientation === "portrait" ? "9:16" : "16:9",
        ...(styleId ? { style_id: styleId } : {}),
        ...(referenceImages.length > 0
          ? { files: referenceImages.map(({ publicUrl }) => ({ url: publicUrl })) }
          : {}),
      });

      setVideos((prev) => [created, ...prev]);
      setPrompt("");
      addNotification("Video generation started!", "success");
    } catch (err: any) {
      addNotification(err?.response?.data?.message || "Failed to start generation", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectActor = (actors: Actor[]) => {
    const picked = actors[0] ?? null;
    setSelectedActor(picked);
    if (picked) setSelectedLabActor(null); // only clear lab actor if we actually picked a regular actor
    setActorsDialogOpen(false);
  };

  const handleSelectAvatarLabActors = (labActors: CustomPhotoAvatar[]) => {
    const picked = labActors[0] ?? null;
    setSelectedLabActor(picked);
    if (picked) setSelectedActor(null); // only clear regular actor if we actually picked a lab actor
    setActorsDialogOpen(false);
  };

  const handleReferenceImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !currentWorkspace || !user) return;

    setIsUploadingReferenceImages(true);
    try {
      for (const file of Array.from(files)) {
        const previewUrl = URL.createObjectURL(file);
        const result = await uploadBackgroundAssetV2(file, user._id, currentWorkspace._id, {
          organizationId: user.organization?._id,
          source: "s3",
        });
        setReferenceImages((prev) => [...prev, { previewUrl, publicUrl: result.url }]);
      }
      addNotification("Reference image added to Video Agent.", "info");
    } catch (err: any) {
      console.error("Error uploading reference image:", err);
      addNotification(err?.response?.data?.message || "Failed to upload reference image", "error");
    } finally {
      setIsUploadingReferenceImages(false);
      if (referenceImageInputRef.current) {
        referenceImageInputRef.current.value = "";
      }
    }
  };

  const handleDownload = async (video: GeneratedVideo) => {
    if (!video.video_url) return;
    const a = document.createElement("a");
    a.href = video.video_url;
    a.download = video.name || "video-agent.mp4";
    a.target = "_blank";
    a.click();
  };

  const isVideoReady = (video: GeneratedVideo) =>
    (video.status === "completed" || video.status === "success") && !!video.video_url;

  const isVideoFailed = (video: GeneratedVideo) => video.status === "failed";

  return (
    <Box className={classes.container}>
      {/* Header */}
      <Box sx={{ width: "100%", maxWidth: "860px", mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ fontFamily: "Lexend, sans-serif", fontWeight: 700, color: "#111" }}
        >
          Video Agent
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#888", fontFamily: "Lexend, sans-serif", mt: 0.5 }}
        >
          Generate avatar-driven videos from a prompt using Video Agent.
        </Typography>
      </Box>

      {/* Past generations */}
      {(videos.length > 0 || isFetchingVideos) && (
        <Box className={classes.carouselArea}>
          <Box className={classes.carouselHeader}>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#444",
                fontFamily: "Lexend, sans-serif",
              }}
            >
              Your generations
            </Typography>
            {isFetchingVideos && <CircularProgress size={14} sx={{ color: "#bbb" }} />}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: 1 }}>
            <InfoIcon sx={{ fontSize: 13, color: "#d5a102" }} />
            <Typography
              sx={{ fontSize: "11px", color: "#d5a102", fontFamily: "Lexend, sans-serif" }}
            >
              Generated video URLs expire after a couple of days. Save any videos you want to keep
              to your library.
            </Typography>
          </Box>
          <Box className={classes.carouselScroll}>
            {videos.map((video) => (
              <Box
                key={video._id}
                className={classes.videoCard}
                onClick={() => setSelectedVideo(video)}
              >
                {isVideoReady(video) ? (
                  <video src={video.video_url} muted preload="metadata" />
                ) : isVideoFailed(video) ? (
                  <Box className={classes.videoCardOverlay}>
                    <SmartDisplayIcon sx={{ fontSize: 24, color: "#e57373" }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "#e57373", fontSize: "10px", fontFamily: "Lexend, sans-serif" }}
                    >
                      Failed
                    </Typography>
                  </Box>
                ) : (
                  <Box className={classes.videoCardOverlay}>
                    <CircularProgress size={20} sx={{ color: "#FFBF00" }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "#888", fontSize: "10px", fontFamily: "Lexend, sans-serif" }}
                    >
                      Generating...
                    </Typography>
                  </Box>
                )}
                <Box className={classes.videoCardInfo}>
                  <span className={classes.videoCardPrompt}>
                    {video.name || video.actorName || "Video"}
                  </span>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Form card */}
      <Box className={classes.formCard}>
        {/* Config row: Avatar + Orientation + Duration */}
        <Box className={classes.configRow}>
          {/* Avatar selector */}
          <Box className={classes.avatarSelector}>
            <Typography className={classes.selectorLabel}>Avatar</Typography>
            <Box className={classes.avatarButton} onClick={() => setActorsDialogOpen(true)}>
              {avatarDisplayImage ? (
                <img
                  src={avatarDisplayImage}
                  alt={avatarDisplayName}
                  className={classes.avatarImage}
                />
              ) : (
                <Box className={classes.avatarPlaceholder}>
                  <PersonIcon sx={{ fontSize: 20 }} />
                </Box>
              )}
              <Typography className={classes.avatarName}>{avatarDisplayName}</Typography>
              <ExpandMoreIcon sx={{ fontSize: 18, color: "#aaa", ml: 0.5 }} />
            </Box>
          </Box>

          {/* Orientation */}
          <Box className={classes.configField}>
            <Typography className={classes.selectorLabel}>Orientation</Typography>
            <Select
              size="small"
              value={orientation}
              onChange={(e) => setOrientation(e.target.value as "landscape" | "portrait")}
              sx={{
                fontFamily: "Lexend, sans-serif",
                fontSize: "14px",
                borderRadius: "12px",
                backgroundColor: "#fafafa",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
              }}
            >
              <MenuItem value="landscape" sx={{ fontFamily: "Lexend, sans-serif" }}>
                Landscape (16:9)
              </MenuItem>
              <MenuItem value="portrait" sx={{ fontFamily: "Lexend, sans-serif" }}>
                Portrait (9:16)
              </MenuItem>
            </Select>
          </Box>

          {/* Duration */}
          <Box className={classes.configField}>
            <Tooltip
              title="The Video Agent will do its best to match this duration, but the final length may vary slightly depending on the content of your prompt."
              placement="top"
              arrow
            >
              <Typography
                className={classes.selectorLabel}
                sx={{ cursor: "default", display: "inline-flex", alignItems: "center", gap: "4px" }}
              >
                Duration (sec)
                <InfoIcon sx={{ fontSize: 12, color: "#bbb" }} />
              </Typography>
            </Tooltip>
            <TextField
              size="small"
              type="number"
              value={duration}
              onChange={(e) => {
                if (e.target.value === "") {
                  setDuration("");
                  return;
                }
                const val = Math.min(120, Math.max(5, Number(e.target.value)));
                setDuration(val);
              }}
              inputProps={{ min: 5, max: 120 }}
              placeholder="e.g. 30"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontFamily: "Lexend, sans-serif",
                  fontSize: "14px",
                  borderRadius: "12px",
                  backgroundColor: "#fafafa",
                  "& fieldset": { borderColor: "#e0e0e0" },
                },
              }}
            />
          </Box>

          {/* Style picker */}
          <Box className={classes.configField}>
            <Typography className={classes.selectorLabel}>Style</Typography>
            <Select
              size="small"
              value={styleId}
              onChange={(e) => setStyleId(e.target.value as string)}
              sx={{
                fontFamily: "Lexend, sans-serif",
                fontSize: "14px",
                borderRadius: "12px",
                backgroundColor: "#fafafa",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
              }}
            >
              <MenuItem value="" sx={{ fontFamily: "Lexend, sans-serif" }}>
                {isFetchingStyles ? "Loading styles..." : "Default style"}
              </MenuItem>
              {styles.map((style) => (
                <MenuItem key={style.style_id} value={style.style_id} sx={{ fontFamily: "Lexend, sans-serif" }}>
                  {style.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Prompt */}
        <Box className={classes.promptArea}>
          <Typography className={classes.selectorLabel}>Prompt</Typography>
          <textarea
            className={classes.promptInput}
            placeholder="Describe your video idea..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
          />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px", mt: "12px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Tooltip title="Attach reference image">
                <IconButton
                  size="small"
                  onClick={() => referenceImageInputRef.current?.click()}
                  disabled={isUploadingReferenceImages}
                >
                  {isUploadingReferenceImages ? (
                    <CircularProgress size={18} />
                  ) : (
                    <AttachFileIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </Tooltip>
              <Typography sx={{ fontSize: "12px", color: "#666", fontFamily: "Lexend, sans-serif" }}>
                Attach images to guide the Video Agent.
              </Typography>
            </Box>
            <input
              ref={referenceImageInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleReferenceImageUpload}
            />
          </Box>
          {referenceImages.length > 0 && (
            <Typography sx={{ fontSize: "12px", color: "#444", fontFamily: "Lexend, sans-serif", mt: 1 }}>
              {referenceImages.length} reference image{referenceImages.length === 1 ? "" : "s"} attached
            </Typography>
          )}
          {referenceImages.length > 0 && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
              {referenceImages.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "relative",
                    width: 72,
                    height: 72,
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <img
                    src={image.previewUrl}
                    alt={`reference-${index}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setReferenceImages((prev) => prev.filter((_, idx) => idx !== index))}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      width: 22,
                      height: 22,
                      p: 0,
                      backgroundColor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "5px", mt: "6px" }}>
            <InfoIcon sx={{ fontSize: 13, color: "#aaa", mt: "1px", flexShrink: 0 }} />
            <Typography
              sx={{
                fontSize: "11px",
                color: "#aaa",
                fontFamily: "Lexend, sans-serif",
                lineHeight: 1.5,
              }}
            >
              Don't want an avatar? Add <strong>"no avatar"</strong> to your prompt and describe
              what you want to generate instead.
            </Typography>
          </Box>
        </Box>

        {mode === "chat" && (
          <Box className={classes.chatPanel}>
            <Box className={classes.chatHeader}>
              <Box>
                <Typography
                  sx={{ fontSize: "13px", fontWeight: 700, color: "#111", fontFamily: "Lexend, sans-serif" }}
                >
                  Video Agent Chat Session
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#666", fontFamily: "Lexend, sans-serif", mt: 0.5 }}>
                  Start a session to get follow-up questions and revisions for the same video project.
                </Typography>
              </Box>
              {session && (
                <Box textAlign="right">
                  <Typography sx={{ fontSize: "12px", color: "#888", fontFamily: "Lexend, sans-serif" }}>
                    Status: {session.status.replace(/_/g, " ")}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "#aaa", fontFamily: "Lexend, sans-serif", mt: 0.5 }}>
                    Last synced: {new Date(session.lastSyncedAt).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>

            {session ? (
              <>
                <Box className={classes.chatMessages}>
                  {session.messages.length === 0 ? (
                    <Typography sx={{ color: "#666", fontFamily: "Lexend, sans-serif" }}>
                      Waiting for the agent to send the first message...
                    </Typography>
                  ) : (
                    session.messages.map((message, index) => (
                      <Box key={`${message.role}-${index}`} className={classes.chatMessageRow}>
                        <Typography className={classes.chatMessageAuthor}>
                          {message.role === "model" ? "Agent" : "You"}
                        </Typography>
                        <Box className={classes.chatBubble}>
                          <Typography sx={{ fontSize: "13px", color: "#222", whiteSpace: "pre-wrap" }}>
                            {message.content}
                          </Typography>
                          {message.resource_ids && message.resource_ids.length > 0 && (
                            <Typography sx={{ fontSize: "11px", color: "#777", mt: 0.75 }}>
                              Resource IDs: {message.resource_ids.join(", ")}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>

                <Box className={classes.chatInputBar}>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={sessionInput}
                    onChange={(e) => setSessionInput(e.target.value)}
                    placeholder={
                      session.status === "waiting_for_input"
                        ? "Reply to the agent's question..."
                        : "Ask the agent for a revision or clarification..."
                    }
                    inputProps={{ style: { fontFamily: "Lexend, sans-serif" } }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={async () => {
                      if (!sessionInput.trim()) {
                        addNotification("Please enter a message", "warning");
                        return;
                      }
                      setIsPostingSessionMessage(true);
                      try {
                        const updatedSession = await postVideoAgentSessionMessage(session._id, {
                          message: sessionInput.trim(),
                          ...(referenceImages.length > 0
                            ? { files: referenceImages.map(({ publicUrl }) => ({ url: publicUrl })) }
                            : {}),
                        });
                        const refreshedSession = updatedSession.session || updatedSession;
                        setSession(refreshedSession);
                        setVideos((prev) =>
                          mergeGeneratedVideos(
                            prev,
                            refreshedSession.videos?.map((item: { generatedVideo: GeneratedVideo }) => item.generatedVideo) ?? []
                          )
                        );
                        setSessionInput("");
                        addNotification("Message sent to Video Agent", "success");
                      } catch (err: any) {
                        addNotification(err?.response?.data?.message || "Failed to send message", "error");
                      } finally {
                        setIsPostingSessionMessage(false);
                      }
                    }}
                    disabled={isPostingSessionMessage || session.status === "failed"}
                    sx={{ ml: 1, whiteSpace: "nowrap" }}
                  >
                    {isPostingSessionMessage ? "Sending..." : "Send"}
                  </Button>
                </Box>
              </>
            ) : (
              <Typography sx={{ color: "#555", fontFamily: "Lexend, sans-serif" }}>
                When you start the chat session, the agent will keep the same video project open and
                accept follow-up requests.
              </Typography>
            )}
          </Box>
        )}

        <Box className={classes.formToolbar}>
          {session && (session.status === "completed" || session.status === "failed") && (
            <Button
              variant="outlined"
              onClick={() => {
                setSession(null);
                setSessionInput("");
              }}
              sx={{ textTransform: "none", fontFamily: "Lexend, sans-serif" }}
            >
              Clear session
            </Button>
          )}
          <Button
            className={classes.generateButton}
            variant="contained"
            disabled={
              isGenerating ||
              isSessionLoading ||
              !prompt.trim() ||
              (mode === "chat" && !!session && session.status !== "completed" && session.status !== "failed")
            }
            onClick={mode === "chat" ? async () => {
              if (!prompt.trim()) {
                addNotification("Please enter a prompt", "warning");
                return;
              }
              if (!currentWorkspace?._id || !user?._id) return;
              setIsSessionLoading(true);
              try {
                const payload = {
                  prompt: prompt.trim(),
                  mode: "chat" as const,
                  workspace: currentWorkspace._id,
                  generatedBy: user._id,
                  name: prompt.trim().slice(0, 60),
                  ...(resolvedAvatarId ? { avatar_id: resolvedAvatarId } : {}),
                  orientation,
                  aspect_ratio: orientation === "portrait" ? "9:16" : "16:9",
                  ...(styleId ? { style_id: styleId } : {}),
                  ...(referenceImages.length > 0
                    ? { files: referenceImages.map(({ publicUrl }) => ({ url: publicUrl })) }
                    : {}),
                };
                const createdSession = await createVideoAgentSession(payload);
                const sessionPayload = createdSession.session || createdSession;
                setSession(sessionPayload);
                setVideos((prev) =>
                  mergeGeneratedVideos(
                    prev,
                    sessionPayload.videos?.map((item: { generatedVideo: GeneratedVideo }) => item.generatedVideo) ?? []
                  )
                );
                setPrompt("");
                addNotification("Video Agent chat session started!", "success");
              } catch (err: any) {
                addNotification(err?.response?.data?.message || "Failed to start chat session", "error");
              } finally {
                setIsSessionLoading(false);
              }
            } : handleGenerate}
            startIcon={
              (isGenerating || isSessionLoading) ? <CircularProgress size={16} sx={{ color: "#000" }} /> : undefined
            }
          >
            {mode === "chat"
              ? session && session.status !== "completed" && session.status !== "failed"
                ? "Session active"
                : isSessionLoading
                ? "Starting..."
                : "Start chat session"
              : isGenerating
              ? "Generating..."
              : "Generate"}
          </Button>
        </Box>
      </Box>

      {/* Empty state */}
      {videos.length === 0 && !isFetchingVideos && (
        <Box className={classes.emptyState} sx={{ mt: 4 }}>
          <SmartDisplayIcon sx={{ fontSize: 48, color: "#ccc" }} />
          <Typography sx={{ color: "#aaa", fontFamily: "Lexend, sans-serif", fontSize: "14px" }}>
            No videos yet — enter a prompt and generate your first one.
          </Typography>
        </Box>
      )}

      {/* Actors Dialog */}
      <ActorsDialog
        open={actorsDialogOpen}
        actors={avatars || []}
        onClose={() => setActorsDialogOpen(false)}
        onSelectActors={handleSelectActor}
        onSelectAvatarLabActors={handleSelectAvatarLabActors}
        selectedActors={selectedActor ? [selectedActor] : []}
        avatarLabActors={selectedLabActor ? [selectedLabActor] : []}
        mode="template"
      />

      {/* Video preview modal */}
      <Dialog
        open={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            fontFamily: "Lexend, sans-serif",
          },
        }}
      >
        {selectedVideo && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontFamily: "Lexend, sans-serif",
                fontSize: "15px",
                fontWeight: 600,
                color: "#111",
                pb: 1,
              }}
            >
              {selectedVideo.name || "Video Agent"}
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {isVideoReady(selectedVideo) && (
                  <IconButton size="small" onClick={() => handleDownload(selectedVideo)}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton size="small" onClick={() => setSelectedVideo(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              {isVideoReady(selectedVideo) ? (
                <video
                  controls
                  autoPlay
                  src={selectedVideo.video_url}
                  className={classes.modalVideo}
                />
              ) : isVideoFailed(selectedVideo) ? (
                <Box className={classes.modalStatusArea}>
                  <SmartDisplayIcon sx={{ fontSize: 48, color: "#e57373" }} />
                  <Typography sx={{ color: "#e57373", fontFamily: "Lexend, sans-serif", mt: 1 }}>
                    Generation failed
                  </Typography>
                </Box>
              ) : (
                <Box className={classes.modalStatusArea}>
                  <CircularProgress size={40} sx={{ color: "#FFBF00" }} />
                  <Typography sx={{ color: "#888", fontFamily: "Lexend, sans-serif", mt: 1.5 }}>
                    Generating your video...
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#aaa" }}>
                    This may take a few minutes
                  </Typography>
                </Box>
              )}
              {selectedVideo.actorName && (
                <Box sx={{ px: 3, py: 1.5, borderTop: "1px solid #f0f0f0" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#888",
                      fontFamily: "Lexend, sans-serif",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Avatar
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#333", fontFamily: "Lexend, sans-serif" }}
                  >
                    {selectedVideo.actorName}
                  </Typography>
                </Box>
              )}
              {selectedVideo.name && (
                <Box sx={{ px: 3, py: 1.5, borderTop: "1px solid #f0f0f0" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#888",
                      fontFamily: "Lexend, sans-serif",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Prompt
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#333", fontFamily: "Lexend, sans-serif" }}
                  >
                    {selectedVideo.name}
                  </Typography>
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default VideoAgent;
